/**
 * MarketingContactenPage — contacten zoeken, status zien, toevoegen aan
 * handmatige lijsten, doorklik naar klant-detail.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMarketingStore, marketingFetch, type MarketingContact } from '../../stores/marketing-store';
import { MarketingNav, MarketingPageHeader, formatDate } from './shared';

export function MarketingContactenPage() {
  const { segments, fetchSegments } = useMarketingStore();
  const [query, setQuery] = useState('');
  const [contacts, setContacts] = useState<MarketingContact[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [targetSegment, setTargetSegment] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    fetchSegments();
  }, [fetchSegments]);

  useEffect(() => {
    let cancelled = false;
    const timeout = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await marketingFetch<{ contacts: MarketingContact[] }>(
          `/api/marketing/contacts?q=${encodeURIComponent(query)}`
        );
        if (!cancelled) setContacts(data.contacts);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Zoeken mislukt');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [query]);

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const addToSegment = async () => {
    if (!targetSegment || selected.size === 0) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await marketingFetch(`/api/marketing/segments?id=${targetSegment}&action=add`, {
        method: 'POST',
        body: { contact_ids: [...selected] },
      });
      setNotice(`${selected.size} contact(en) toegevoegd aan de lijst.`);
      setSelected(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Toevoegen mislukt');
    } finally {
      setBusy(false);
    }
  };

  const manualSegments = segments.filter((s) => s.type === 'manual');

  return (
    <>
      <div className="flex-1 overflow-y-auto">
        <MarketingPageHeader title="Contacten" subtitle="Het gesyncte Kleda-klantenbestand (nachtelijke sync uit Salesforce)" />
        <MarketingNav />

        <div className="p-6 space-y-4">
          <div className="flex items-center gap-3">
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Zoek op naam, e-mail of postcode…"
              className="w-full max-w-md border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
            {selected.size > 0 && (
              <div className="flex items-center gap-2">
                <select
                  value={targetSegment}
                  onChange={(e) => setTargetSegment(e.target.value)}
                  className="border border-gray-300 rounded-lg px-2 py-2 text-sm bg-white"
                >
                  <option value="">— kies lijst —</option>
                  {manualSegments.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                <button
                  onClick={addToSegment}
                  disabled={busy || !targetSegment}
                  className="px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 whitespace-nowrap"
                >
                  {busy ? 'Bezig…' : `${selected.size} toevoegen`}
                </button>
              </div>
            )}
          </div>

          {error && <div className="bg-red-50 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>}
          {notice && <div className="bg-emerald-50 text-emerald-700 text-sm rounded-lg px-4 py-3">{notice}</div>}

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs text-gray-400">
                  <th className="px-4 py-2 w-8" />
                  <th className="px-4 py-2 font-medium">Naam</th>
                  <th className="px-4 py-2 font-medium">E-mail</th>
                  <th className="px-4 py-2 font-medium">Plaats</th>
                  <th className="px-4 py-2 font-medium">Laatste service</th>
                  <th className="px-4 py-2 font-medium">Orders</th>
                  <th className="px-4 py-2 font-medium">Recency</th>
                  <th className="px-4 py-2 font-medium" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {contacts.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleSelect(c.id)} />
                    </td>
                    <td className="px-4 py-2 text-gray-900">
                      {[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}
                      {c.blacklisted && <span className="ml-2 text-xs text-red-600">blacklist</span>}
                      {c.open_complaint && <span className="ml-2 text-xs text-amber-600">open klacht</span>}
                    </td>
                    <td className="px-4 py-2 text-gray-600">{c.email}</td>
                    <td className="px-4 py-2 text-gray-500">{c.city ?? '—'}</td>
                    <td className="px-4 py-2 text-gray-500">{formatDate(c.last_service_date)}</td>
                    <td className="px-4 py-2 text-gray-500">{c.order_count}</td>
                    <td className="px-4 py-2 text-gray-500">{c.recency ?? '—'}</td>
                    <td className="px-4 py-2">
                      {c.sf_account_id && (
                        <Link to={`/klanten/${c.sf_account_id}?tab=marketing`} className="text-indigo-600 hover:underline text-xs">
                          Klant-detail
                        </Link>
                      )}
                    </td>
                  </tr>
                ))}
                {contacts.length === 0 && !loading && (
                  <tr>
                    <td colSpan={8} className="px-4 py-6 text-center text-gray-400">
                      {query ? 'Geen contacten gevonden.' : 'Nog geen contacten — de nachtelijke sync vult het bestand.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
